/**
 * VIVA HUB app launcher dashboard
 */

import React from 'react'
import { Link } from 'react-router-dom'
import HubLayout from '../components/hub/HubLayout'
import HubAppIcon from '../components/hub/HubAppIcon'
import { useSchedulerAuth } from '../contexts/SchedulerAuthContext'
import { getAccessibleApps, getAppPath } from '../constants/hub-apps'
import { HUB_SUPPORT_APP } from '../constants/hub-support-app'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card'

function AppCard({ to, appId, name, description }) {
  return (
    <Link
      to={to}
      className="group block rounded-xl focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
    >
      <Card className="h-full transition-all hover:border-primary/40 hover:shadow-md">
        <CardHeader className="flex flex-row items-start gap-4 space-y-0 pb-3">
          <div
            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary transition-colors group-hover:bg-primary/15"
            aria-hidden="true"
          >
            <HubAppIcon appId={appId} className="h-5 w-5" />
          </div>
          <div className="min-w-0 flex-1 space-y-1">
            <CardTitle className="text-base">{name}</CardTitle>
            <CardDescription className="leading-snug">{description}</CardDescription>
          </div>
        </CardHeader>
        <CardFooter>
          <span className="text-sm font-medium text-primary group-hover:underline">Open →</span>
        </CardFooter>
      </Card>
    </Link>
  )
}

export default function HubDashboard() {
  const { user } = useSchedulerAuth()
  const accessibleApps = getAccessibleApps(user)
  const firstName = (user?.name || '').trim().split(/\s+/)[0]

  return (
    <HubLayout>
      <div className="mb-8">
        <Badge variant="secondary" className="mb-3">
          VIVA HUB
        </Badge>
        <h1 className="text-2xl font-bold tracking-tight text-foreground sm:text-3xl">
          {firstName ? `Welcome back, ${firstName}` : 'Welcome back'}
        </h1>
        <p className="mt-2 max-w-2xl text-sm text-muted-foreground sm:text-base">
          Choose an app to continue. Your access depends on the groups assigned to your account.
        </p>
      </div>

      {accessibleApps.length === 0 ? (
        <Card className="mb-6 border-amber-200 bg-amber-50 text-amber-950">
          <CardContent className="pt-6 text-sm">
            You do not have access to any apps yet. Contact an administrator to request access, or open
            IT Technical Support below.
          </CardContent>
        </Card>
      ) : null}

      <section aria-labelledby="hub-apps-heading">
        <div className="mb-4 flex items-end justify-between gap-3">
          <h2
            id="hub-apps-heading"
            className="text-sm font-semibold uppercase tracking-wide text-muted-foreground"
          >
            Your apps
          </h2>
          <span className="text-xs text-muted-foreground tabular-nums">
            {accessibleApps.length} available
          </span>
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {accessibleApps.map((app) => (
            <AppCard
              key={app.id}
              to={getAppPath(app, user)}
              appId={app.id}
              name={app.name}
              description={app.description}
            />
          ))}

          <AppCard
            to={HUB_SUPPORT_APP.path}
            appId={HUB_SUPPORT_APP.id}
            name={HUB_SUPPORT_APP.name}
            description={HUB_SUPPORT_APP.description}
          />
        </div>
      </section>
    </HubLayout>
  )
}
